import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, LogOut, LayoutDashboard, Star } from "lucide-react";
import { toast } from "sonner";
import { api, setChildId } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ParentGate } from "../components/ParentGate";
import { Avatar } from "../components/Avatar";
import { ChildForm } from "../components/ChildForm";

export default function Profiles() {
  const [kids, setKids] = useState(null);
  const [adding, setAdding] = useState(false);
  const [gate, setGate] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/children").then(({ data }) => setKids(data));
  useEffect(() => { load(); }, []);

  const create = async (form) => {
    try { await api.post("/children", form); } catch (e) {
      if (e?.response?.status === 402) return toast.error(e.response.data.detail, { action: { label: "查看方案", onClick: () => nav("/pricing") } });
      return toast.error("建立失敗，請再試一次");
    }
    toast.success("Profile created");
    setAdding(false);
    load();
  };

  const play = (id) => {
    setChildId(id);
    nav("/halls");
  };

  return (
    <div className="min-h-screen paper-bg" data-testid="profiles-page">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="font-brush text-4xl text-[var(--vermilion)]">小書童</div>
        <div className="flex gap-2 font-en">
          <button data-testid="profiles-parent-btn" onClick={() => setGate(true)} className="px-4 h-11 rounded-full bg-white border-2 border-[var(--line)] flex items-center gap-2 text-sm font-semibold hover:bg-amber-50">
            <LayoutDashboard size={16} /> Parent area
          </button>
          <button data-testid="logout-btn" onClick={() => logout().then(() => nav("/"))} className="px-4 h-11 rounded-full bg-white border-2 border-[var(--line)] flex items-center gap-2 text-sm font-semibold hover:bg-amber-50">
            <LogOut size={16} />
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 pt-6 pb-20">
        <h1 className="font-serif-tc text-4xl sm:text-5xl font-black text-[var(--tea)]">誰要去冒險？</h1>
        <p className="font-en text-stone-600 mt-2">Hi {user?.name?.split(" ")[0]} — pick a learner, or add one.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
          {(kids || []).map((k, i) => (
            <motion.button key={k.child_id} data-testid={`profile-card-${k.child_id}`} onClick={() => play(k.child_id)}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} whileHover={{ y: -6 }} whileTap={{ scale: 0.95 }}
              className="scroll-card p-6 flex flex-col items-center gap-3">
              <div className="text-7xl w-28 h-28 rounded-full bg-amber-50 border-4 border-amber-200 flex items-center justify-center overflow-hidden"><Avatar value={k.avatar} /></div>
              <div className="font-serif-tc text-2xl font-bold">{k.nickname}</div>
              <div className="flex items-center gap-1 text-amber-600 font-bold"><Star size={18} fill="currentColor" /> {k.stars}</div>
            </motion.button>
          ))}
          <motion.button data-testid="add-profile-btn" onClick={() => setAdding(true)} whileHover={{ y: -6 }}
            className="rounded-[1.75rem] border-2 border-dashed border-amber-400 p-6 flex flex-col items-center justify-center gap-3 text-amber-700 min-h-[220px] hover:bg-amber-50">
            <Plus size={40} />
            <span className="font-en font-bold">Add learner</span>
          </motion.button>
        </div>
        {kids && kids.length === 0 && <p className="font-en text-stone-500 mt-6" data-testid="no-profiles-msg">No learners yet. Only a nickname is needed — no personal details.</p>}
      </main>
      <ChildForm open={adding} onOpenChange={setAdding} onSubmit={create} />
      <ParentGate open={gate} onOpenChange={setGate} onPass={() => nav("/parent")} />
    </div>
  );
}
